import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { blogService } from '../service/blogService';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MetalRateStrip from '../components/MetalRateStrip';
import './BlogAdminDashboard.css';

const PRESET_IMAGES = [
  { label: 'Gold Bullion & Coins', url: 'https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&w=1200&q=80' },
  { label: 'Hallmarked Jewellery Craft', url: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=1200&q=80' },
  { label: 'Diamond Setting & CAD', url: 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&w=1200&q=80' },
  { label: 'Silver Bullion & Artefacts', url: 'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?auto=format&fit=crop&w=1200&q=80' },
];

const BlogAdminDashboard = () => {
  const navigate = useNavigate();
  const [author, setAuthor] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null); // null = table view, 'new' = create, or post object
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: 'Bullion & Pricing Dynamics',
    categorySlug: 'pricing',
    author: '',
    authorRole: 'JMI Bullion Research Desk',
    readTime: '5 min read',
    coverImage: PRESET_IMAGES[0].url,
    excerpt: '',
    content: '',
    tags: 'Gold Rates, Bullion, B2B Trade',
    status: 'published',
  });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', msg: '' });

  // Guard: require author login
  useEffect(() => {
    const raw = sessionStorage.getItem('jmi_blog_author');
    if (!raw) {
      navigate('/blog-portal/login');
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setAuthor(parsed);
      setFormData((prev) => ({
        ...prev,
        author: parsed.name || 'JMI Contributor',
      }));
    } catch {
      navigate('/blog-portal/login');
    }
  }, [navigate]);

  const loadPosts = async () => {
    setLoading(true);
    const data = await blogService.getAllPostsForAdmin();
    setPosts(data);
    setLoading(false);
  };

  useEffect(() => {
    if (author) {
      loadPosts();
    }
  }, [author]);

  const showFeedback = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback({ type: '', msg: '' }), 4000);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('jmi_blog_author');
    navigate('/blog-portal/login');
  };

  const handleStartCreate = () => {
    setEditingPost('new');
    setFormData({
      title: '',
      slug: '',
      category: 'Bullion & Pricing Dynamics',
      categorySlug: 'pricing',
      author: author?.name || 'JMI Research Contributor',
      authorRole: 'JMI Industry Analyst',
      readTime: '5 min read',
      coverImage: PRESET_IMAGES[0].url,
      excerpt: '',
      content: '',
      tags: 'Gold, B2B Jewellery, Market Trends',
      status: 'published',
    });
  };

  const handleStartEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      slug: post.slug || '',
      category: post.category || 'Bullion & Pricing Dynamics',
      categorySlug: post.categorySlug || 'pricing',
      author: post.author || author?.name || '',
      authorRole: post.authorRole || 'JMI Industry Analyst',
      readTime: post.readTime || '5 min read',
      coverImage: post.coverImage || PRESET_IMAGES[0].url,
      excerpt: post.excerpt || '',
      content: post.content || '',
      tags: Array.isArray(post.tags) ? post.tags.join(', ') : post.tags || '',
      status: post.status || 'published',
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'title' && editingPost === 'new' && !prev.slug) {
        next.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      }
      if (name === 'category') {
        const catMap = {
          'Compliance & Hallmarking': 'compliance',
          'Bullion & Pricing Dynamics': 'pricing',
          'Manufacturing Technology': 'technology',
          'B2B Sourcing & Trade': 'sourcing',
        };
        next.categorySlug = catMap[value] || 'general';
      }
      return next;
    });
  };

  const handleSavePost = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      showFeedback('error', 'Article title and body content are required.');
      return;
    }

    setSaving(true);
    try {
      const tagList = formData.tags
        ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [];

      const payload = {
        ...formData,
        tags: tagList,
        title: formData.title.trim(),
        excerpt: formData.excerpt.trim() || formData.title.trim(),
        slug: formData.slug.trim() || formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      };

      if (editingPost === 'new') {
        await blogService.createPost(payload);
        showFeedback('success', 'New article published successfully to JMI blog!');
      } else if (editingPost?.id) {
        await blogService.updatePost(editingPost.id, payload);
        showFeedback('success', 'Article updated successfully!');
      }

      setEditingPost(null);
      await loadPosts();
    } catch (err) {
      console.error('Error saving post:', err);
      showFeedback('error', `Failed to save article: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (postId, postTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${postTitle}"?`)) return;
    try {
      await blogService.deletePost(postId);
      showFeedback('success', 'Article removed.');
      await loadPosts();
    } catch (err) {
      showFeedback('error', 'Failed to delete article.');
    }
  };

  return (
    <div className="app-wrapper blog-admin-wrapper">
      <MetalRateStrip />
      <Navbar />

      <main className="blog-admin-main">
        <div className="jmi-container">
          {/* Top Bar with Author Session Info */}
          <div className="admin-studio-header">
            <div>
              <div className="studio-tag">JMI EDITORIAL PUBLISHING STUDIO</div>
              <h1 className="studio-title">Blog Content & Industry Reports Manager</h1>
              <p className="studio-desc">
                Logged in as <strong>{author?.name}</strong> ({author?.email}) • <em>{author?.role}</em>
              </p>
            </div>

            <div className="studio-actions">
              {editingPost ? (
                <button className="btn btn-secondary" onClick={() => setEditingPost(null)}>
                  ← Back to Articles List
                </button>
              ) : (
                <button className="btn btn-primary" onClick={handleStartCreate}>
                  + Write New Blog Post
                </button>
              )}
              <button className="btn-author-logout" onClick={handleLogout} title="Sign Out">
                Sign Out
              </button>
            </div>
          </div>

          {/* Feedback message banner */}
          {feedback.msg && (
            <div className={`studio-alert ${feedback.type}`}>
              {feedback.type === 'success' ? '✓' : '⚠️'} {feedback.msg}
            </div>
          )}

          {/* View Mode 1: Editor Form */}
          {editingPost ? (
            <div className="studio-editor-card">
              <div className="editor-card-header">
                <h2>{editingPost === 'new' ? '✍️ Draft & Publish New Industry Report' : '✏️ Edit Report'}</h2>
                <span className="editor-hint">Supports Markdown headings, blockquotes, and lists</span>
              </div>

              <form onSubmit={handleSavePost} className="studio-form">
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Article Headline / Title *</label>
                    <input
                      type="text"
                      name="title"
                      placeholder="e.g. BIS Hallmarking 2.0: What Retail Jewellers Need to Know"
                      value={formData.title}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>URL Slug</label>
                    <input
                      type="text"
                      name="slug"
                      placeholder="e.g. bis-hallmarking-2-0-retail-jewellers"
                      value={formData.slug}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-grid-3">
                  <div className="form-field">
                    <label>Topic Category</label>
                    <select name="category" value={formData.category} onChange={handleFormChange}>
                      <option value="Compliance & Hallmarking">Compliance & Hallmarking</option>
                      <option value="Bullion & Pricing Dynamics">Bullion & Pricing Dynamics</option>
                      <option value="Manufacturing Technology">Manufacturing Technology</option>
                      <option value="B2B Sourcing & Trade">B2B Sourcing & Trade</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Estimated Read Time</label>
                    <input
                      type="text"
                      name="readTime"
                      placeholder="e.g. 5 min read"
                      value={formData.readTime}
                      onChange={handleFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>Publication Status</label>
                    <select name="status" value={formData.status} onChange={handleFormChange}>
                      <option value="published">Published (Visible on Live Blog)</option>
                      <option value="draft">Draft (Saved in Studio Only)</option>
                    </select>
                  </div>
                </div>

                <div className="form-field">
                  <label>Featured Cover Image URL</label>
                  <input
                    type="url"
                    name="coverImage"
                    placeholder="https://images.unsplash.com/..."
                    value={formData.coverImage}
                    onChange={handleFormChange}
                  />
                  <div className="preset-images-row">
                    <span className="preset-label">Quick Presets:</span>
                    {PRESET_IMAGES.map((img, idx) => (
                      <button
                        type="button"
                        key={idx}
                        className="preset-btn"
                        onClick={() => setFormData((p) => ({ ...p, coverImage: img.url }))}
                      >
                        {img.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-field">
                  <label>Summary / Excerpt (Shows on cards & search previews) *</label>
                  <textarea
                    name="excerpt"
                    rows="2"
                    placeholder="A concise 2-sentence executive summary of the report..."
                    value={formData.excerpt}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Full Body Article Content (Markdown supported) *</label>
                  <textarea
                    name="content"
                    rows="14"
                    className="content-editor-textarea"
                    placeholder="## Main Heading&#10;&#10;Write the full body of the analysis here. You can use ## for section headings, ### for subheadings, > for quote callouts, and bullet points..."
                    value={formData.content}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Author Display Name</label>
                    <input
                      type="text"
                      name="author"
                      value={formData.author}
                      onChange={handleFormChange}
                    />
                  </div>

                  <div className="form-field">
                    <label>Search & SEO Tags (comma-separated)</label>
                    <input
                      type="text"
                      name="tags"
                      placeholder="Gold, HUID, BIS, Bullion, RTGS"
                      value={formData.tags}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="editor-form-actions">
                  <button type="submit" disabled={saving} className="btn btn-primary btn-lg">
                    {saving ? 'Publishing to Blog...' : formData.status === 'published' ? '🚀 Publish Article Live' : '💾 Save as Draft'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setEditingPost(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* View Mode 2: Articles List Table */
            <div className="studio-articles-table-card">
              <div className="table-header-row">
                <h2>Published & Draft Articles ({posts.length})</h2>
                <div className="table-sub-link">
                  <Link to="/blog" target="_blank">
                    Open Live Blog Page ↗
                  </Link>
                </div>
              </div>

              {loading ? (
                <div className="studio-loading">
                  <div className="spinner"></div>
                  <span>Loading articles from database...</span>
                </div>
              ) : posts.length === 0 ? (
                <div className="studio-empty">
                  <h3>No articles created yet.</h3>
                  <button className="btn btn-primary" onClick={handleStartCreate}>
                    Write Your First Post
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="studio-table">
                    <thead>
                      <tr>
                        <th>Article Details</th>
                        <th>Category</th>
                        <th>Author</th>
                        <th>Status</th>
                        <th>Published Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posts.map((p) => (
                        <tr key={p.id || p.slug}>
                          <td>
                            <div className="table-article-info">
                              <strong className="table-article-title">{p.title}</strong>
                              <span className="table-article-slug">/{p.slug}</span>
                            </div>
                          </td>
                          <td>
                            <span className="table-category-tag">{p.category}</span>
                          </td>
                          <td>
                            <span className="table-author-name">{p.author}</span>
                          </td>
                          <td>
                            <span className={`status-badge-pill ${p.status === 'published' ? 'published' : 'draft'}`}>
                              {p.status ? p.status.toUpperCase() : 'PUBLISHED'}
                            </span>
                          </td>
                          <td>
                            <span className="table-date">
                              {p.publishedAt || p.createdAt?.toDate
                                ? new Date(p.publishedAt || p.createdAt.toDate()).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                  })
                                : '—'}
                            </span>
                          </td>
                          <td>
                            <div className="table-actions">
                              <Link to={`/blog/${p.slug}`} className="btn-table-view" target="_blank">
                                View
                              </Link>
                              <button className="btn-table-edit" onClick={() => handleStartEdit(p)}>
                                Edit
                              </button>
                              <button
                                className="btn-table-del"
                                onClick={() => handleDeletePost(p.id, p.title)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogAdminDashboard;
